import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import "./MyWishlist.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faTimes } from "@fortawesome/free-solid-svg-icons";
import TrashCan from "../../assets/icons/Trash.svg";
import Breadcrums from "../../components/breadcrums/Breadcrums";
import ApiService from "../../components/Api_services";
import DeleteItem from "../../components/deleteitem/DeleteItem";
import FallbackImg from "../../components/FallbackImg";
import { useSelector } from "react-redux";
import { ACCESS_KEYS } from "../../constants";
import { hasAccess } from "../../utils/Permission";

function getPagination(current, total) {
  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 4) pages.push("...");
    let start, end;
    if (current <= 4) {
      start = 2;
      end = 5;
    } else if (current >= total - 3) {
      start = total - 4;
      end = total - 1;
    } else {
      start = current - 1;
      end = current + 1;
    }
    for (let i = start; i <= end; i++) {
      if (i > 1 && i < total) pages.push(i);
    }
    if (current < total - 3) pages.push("...");
    pages.push(total);
  }
  return pages;
}

const MyWishlistv1 = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const page =
    parseInt(searchParams.get("page"), 10) > 0
      ? parseInt(searchParams.get("page"), 10)
      : 1;
  const activeTab = searchParams.get("tab") || "wishlist";
  // State variables
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(page);
  const [showDeleteItem, setShowDeleteItem] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);

  const sub_role = useSelector((state) => state.auth.sub_role) || [];
  const is_subuser =
    useSelector((state) => state.auth.user?.is_subuser) || false;
  const canOrder = !is_subuser || hasAccess(sub_role, ACCESS_KEYS.MY_ORDER);
  const canView = !is_subuser || hasAccess(sub_role, ACCESS_KEYS.MY_ORDER);

  // ensure defaults exist in URL on mount
  useEffect(() => {
    const params = Object.fromEntries([...searchParams]);
    let changed = false;
    if (!params.tab) {
      params.tab = "wishlist";
      changed = true;
    }
    if (!params.page) {
      params.page = 1;
      changed = true;
    }
    if (changed) {
      setSearchParams(params);
    }
  }, []);

  // keep local currentPage in sync with URL page
  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  // Fetch items when tab, page, or searchQuery changes
  useEffect(() => {
    fetchItems();
  }, [activeTab, page, searchQuery]);

  // Function to fetch items
  const fetchItems = async () => {
    setLoading(true);
    try {
      let response;

      // use URL-derived page (page) instead of local currentPage for API calls
      const pageToFetch = page;

      if (activeTab === "wishlist") {
        response = await ApiService.getMyWishlist(
          pageToFetch,
          itemsPerPage,
          searchQuery
        );
      } else if (activeTab === "favourite") {
        response = await ApiService.getMyFavourite(
          pageToFetch,
          itemsPerPage,
          searchQuery
        );
      } else if (activeTab === "buyagain") {
        response = await ApiService.getMyRepeatOrder(
          pageToFetch,
          itemsPerPage,
          searchQuery
        );
      }

      if (response.status === 200) {
        let fetchedItems = [];
        if (response.data?.items) {
          fetchedItems = response.data.items;
        } else if (Array.isArray(response.data)) {
          fetchedItems = response.data;
        }
        setItems(fetchedItems);
        setTotalPages(response.pagination?.total_pages || 1);
      } else {
        setError(response.message || "Failed to load items");
        setItems([]);
      }
    } catch (err) {
      console.error(`Error loading ${activeTab}:`, err);
      setError(`Failed to load ${activeTab}. Please try again.`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const isOutOfStock = (item) => {
    const stock = Number(item?.stock_quantity ?? item?.stock ?? NaN);
    const brandPrice = Number(
      item?.brand_price ?? item?.pricing?.brand_price ?? NaN
    );

    const stockIsZeroOrLess = Number.isFinite(stock) ? stock <= 0 : false;
    const brandPriceIsZero = Number.isFinite(brandPrice)
      ? brandPrice === 0
      : false;

    return stockIsZeroOrLess || brandPriceIsZero;
  };

  const isDiscontinued = (item) => {
    const stock = Number(item?.stock_quantity ?? item?.stock ?? NaN);
    const brandPrice = Number(
      item?.brand_price ?? item?.pricing?.brand_price ?? NaN
    );

    const out =
      (Number.isFinite(stock) ? stock <= 0 : false) ||
      (Number.isFinite(brandPrice) ? brandPrice === 0 : false);
    return out && String(item?.status) === "2";
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", 1);
    navigate(`${location?.pathname || "/mywishlist"}?${params.toString()}`, {
      replace: true,
    });
  };

  const clearSearch = () => {
    setSearchQuery("");
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", 1);
    navigate(`${location?.pathname || "/mywishlist"}?${params.toString()}`, {
      replace: true,
    });
  };

  const handleTabChange = (tab) => {
    navigate(`/mywishlist?tab=${tab}&page=1`);
  };

  // Handle page change
  const handlePageChange = (p) => {
    const params = new URLSearchParams();
    params.set("tab", activeTab);
    params.set("page", p);
    navigate(`/mywishlist?${params.toString()}`);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Handle remove item
  const handleRemoveItem = async (item) => {
    try {
      setLoading(true);
      const bundle_id = item.bundle_id ?? item.package_id ?? 0;
      const item_id = item.id ?? null;
      let response;

      if (activeTab === "wishlist") {
        const wishlist_id = item.cw_id ?? item.wishlist_id ?? null;
        response = await ApiService.removeFromWishlist({
          wishlist_id,
          item_id,
          bundle_id,
        });
      } else {
        const favorite_id = item.cf_id ?? item.favorite_id ?? null;
        response = await ApiService.removeFromFavorite({
          favorite_id,
          item_id,
          bundle_id,
        });
      }

      if (response?.status === 200) {
        await fetchItems();
      } else {
        setError(response?.message || "Failed to remove item");
      }
    } catch (err) {
      console.error("Error removing item:", err);
      setError("Failed to remove item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const wishlistResponse = await ApiService.getCountAvailableWishlistItem(
          {}
        );

        if (wishlistResponse?.status === 200) {
          const wishlistTotal =
            wishlistResponse.data.available_wishlist_items || 0;
          setWishlistCount(wishlistTotal);
        }
      } catch (error) {
        console.error("Error fetching counts:", error);
      }
    };

    fetchCounts();
  }, []);

  return (
    <div className="mywishlist">
      <Breadcrums />
      <div className="mywishlist-content">
        {canView ? (
          <>
            <div className="mywishlist-tab-container">
              {/* Main tabs for Wishlist/Favorite */}
              <div className="mywishlist-tabs">
                <div
                  className={`wishlist-tab${
                    activeTab === "wishlist" ? " active-tab" : ""
                  }`}
                  onClick={() => handleTabChange("wishlist")}
                >
                  My Wishlist
                  {wishlistCount > 0 && (
                    <div className="unread-tag">{wishlistCount}</div>
                  )}
                </div>
                <div
                  className={`favourite-tab${
                    activeTab === "favourite" ? " active-tab" : ""
                  }`}
                  onClick={() => handleTabChange("favourite")}
                >
                  Favourite
                </div>

                <div
                  className={`buyagain-tab${
                    activeTab === "buyagain" ? " active-tab" : ""
                  }`}
                  onClick={() => handleTabChange("buyagain")}
                >
                  Buy Again
                </div>
              </div>

              <form className="searchbar-mywishlist" onSubmit={handleSearch}>
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  style={{
                    color: "#798777",
                    fontSize: "18px",
                  }}
                />
                <input
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                {searchQuery && (
                  <FontAwesomeIcon
                    icon={faTimes}
                    onClick={clearSearch}
                    style={{
                      color: "#798777",
                      fontSize: "16px",
                      cursor: "pointer",
                      marginRight: "10px",
                    }}
                  />
                )}
                <button type="submit" style={{ display: "none" }}></button>
              </form>
            </div>

            <div className="mywishlist-line"></div>

            {items.length === 0 && loading ? (
              <div>Loading...</div>
            ) : error ? (
              <div>{error}</div>
            ) : items.length === 0 ? (
              <div>
                {activeTab === "wishlist"
                  ? `Your wishlist is empty.`
                  : `Your favorites list is empty.`}
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.item_id}
                  className={`mywishlist-item ${
                    isOutOfStock(item) ? "out-of-stock-item" : ""
                  }`}
                  onClick={(e) => {
                    // Check if the click is not on interactive elements
                    if (
                      !e.target.closest(".mywishlist-delete") &&
                      !e.target.closest(".addcart-button") &&
                      !e.target.closest("a")
                    ) {
                      const isBundle =
                        String(item.is_bundle) === "1" ||
                        Boolean(item.package_id) ||
                        Boolean(item.bundle_id);
                      const packageId = item.package_id ?? item.bundle_id ?? item.id;
                      {
                        const params = new URLSearchParams();
                        params.set("isBundle", isBundle ? "1" : "0");
                        const bundleId = item.bundle_id ?? item.package_id ?? null;
                        if (bundleId) params.set("bundleId", bundleId);
                        navigate(
                          `/productdetail/${item.id}?${params.toString()}`,
                          { state: { isBundle, packageId } },
                        );
                      }
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="mywishlist-left">
                    <div className="wishlist-image-container">
                      {isOutOfStock(item) && (
                        <div
                          className={
                            isDiscontinued(item)
                              ? "document-out-of-stock discontinued-bg"
                              : "document-out-of-stock outofstock-bg"
                          }
                        >
                          {isDiscontinued(item)
                            ? "Discontinued"
                            : "Out of stock"}
                        </div>
                      )}
                      <FallbackImg
                        src={
                          item.thumb_url || "../../assets/img/placeholder.jpg"
                        }
                        alt={item.item_name}
                        className="wishlistitem-image"
                      />
                    </div>
                  </div>

                  <div className="mywishlist-center">
                    <div className="mywishlist-code">{item.item_code}</div>
                    <div className="mywishlist-name">{item.item_name}</div>
                  </div>

                  <div className="mywishlist-right">
                    <span
                      className="mywishlist-delete"
                      onClick={(e) => {
                        if (!canOrder) return;
                        e.stopPropagation();
                        setShowDeleteItem(item);
                      }}
                      style={{
                        opacity: canOrder ? 1 : 0.5,
                        cursor: canOrder ? "pointer" : "not-allowed",
                      }}
                      title={canOrder ? "" : "No permission"}
                    >
                      <FallbackImg
                        src={TrashCan}
                        alt="trashcan"
                        width="20"
                        height="20"
                      />
                    </span>
                    <div className="mywishlist-restock-container">
                      {activeTab === "wishlist" && !isOutOfStock(item) && (
                        <div className="mywishlist-restock">
                          &#x25CF; Your item is now restocked!
                        </div>
                      )}
                      {!isOutOfStock(item) && (
                        <button
                          type="button"
                          className="addcart-button"
                          onClick={(e) => {
                            if (!canOrder) return;
                            e.stopPropagation();
                            const isBundle =
                              String(item.is_bundle) === "1" ||
                              Boolean(item.package_id) ||
                              Boolean(item.bundle_id);
                            const packageId = item.package_id ?? item.bundle_id ?? item.id;
                            {
                              const params = new URLSearchParams();
                              params.set("isBundle", isBundle ? "1" : "0");
                              const bundleId = item.package_id ?? item.bundle_id ?? item.id;
                              if (bundleId) params.set("bundleId", bundleId);
                              navigate(
                                `/productdetail/${item.id}?${params.toString()}`,
                                { state: { isBundle, packageId } },
                              );
                            }
                          }}
                          disabled={!canOrder}
                          style={{
                            opacity: canOrder ? 1 : 0.5,
                            cursor: canOrder ? "pointer" : "not-allowed",
                          }}
                          title={canOrder ? "" : "No permission"}
                        >
                          ADD TO CART
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            {showDeleteItem && (
              <DeleteItem
                item={showDeleteItem}
                onClose={() => setShowDeleteItem(false)}
                onDelete={handleRemoveItem}
              />
            )}

            {/* Pagination */}
            {!loading && items.length > 0 && (
              <div className="pagination-modern">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <span className="arrow">&lt;</span>
                  <span className="prevnext-text">Prev</span>
                </button>
                {getPagination(page, totalPages).map((p, idx) =>
                  p === "..." ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="pagination-ellipsis"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={`page-${p}-${idx}`}
                      className={`pagination-btn${
                        page === p ? " pagination-current" : ""
                      }`}
                      onClick={() => handlePageChange(p)}
                      disabled={page === p}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                >
                  <span className="prevnext-text">Next</span>
                  <span className="arrow">&gt;</span>
                </button>
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              minHeight: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ color: "#b71c1c", fontWeight: 600 }}>
              You do not have permission to view this section.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyWishlistv1;
